import type { Trace } from '@/features/traceability/type/Tool';
import { computeTraceGroupings } from '../components/tracesViewStats';

type CountEntry = [string, number];

export type ComputedStats = {
  totalTraces: number;
  totalPieces: number;
  totalDistance: number;
  totalVolume: number;
  totalMachiningSeconds: number;
  totalPieceMachiningSeconds: number;
  avgMachiningSeconds: number | null;
  avgPieceSeconds: number | null;
  machineUsage: CountEntry[];
  operatorUsage: CountEntry[];
  changeReasons: CountEntry[];
  recentTraces: Trace[];
};

const sumNumbers = (values: Array<number | null | undefined>): number =>
  values.reduce<number>((acc, value) => acc + (value ?? 0), 0);

const average = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => value !== null && value !== undefined);
  if (!filtered.length) return null;
  return filtered.reduce((acc, value) => acc + value, 0) / filtered.length;
};

export function computeTraceStats(traces: Trace[]): ComputedStats {
  if (!traces.length) {
    return {
      totalTraces: 0,
      totalPieces: 0,
      totalDistance: 0,
      totalVolume: 0,
      totalMachiningSeconds: 0,
      totalPieceMachiningSeconds: 0,
      avgMachiningSeconds: null,
      avgPieceSeconds: null,
      machineUsage: [],
      operatorUsage: [],
      changeReasons: [],
      recentTraces: [],
    };
  }

  const totalPieces = sumNumbers(traces.map((trace) => trace.machinedPieces));
  const totalDistance = sumNumbers(traces.map((trace) => trace.machinedDistance));
  const totalVolume = sumNumbers(traces.map((trace) => trace.machinedVolume));
  const totalMachiningSeconds = sumNumbers(traces.map((trace) => trace.totalMachiningDuration));
  const totalPieceMachiningSeconds = sumNumbers(
    traces.map((trace) => trace.pieceMachiningDuration)
  );
  const avgMachiningSeconds = average(traces.map((trace) => trace.totalMachiningDuration));
  const avgPieceSeconds = average(traces.map((trace) => trace.pieceMachiningDuration));

  return {
    totalTraces: traces.length,
    totalPieces,
    totalDistance,
    totalVolume,
    totalMachiningSeconds,
    totalPieceMachiningSeconds,
    avgMachiningSeconds,
    avgPieceSeconds,
    ...computeTraceGroupings(traces),
    recentTraces: traces.slice(0, 50),
  };
}
